import {
  Component,
  computed,
  DoCheck,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { CommonModule } from '@angular/common';
import { NgIf } from '@angular/common';
import { KeyArea, KeyphraseRow } from '../../core/interfaces/KeyphraseExtraction';
import { Dialog } from 'primeng/dialog';
import { Ripple } from 'primeng/ripple';
import { TextareaModule } from 'primeng/textarea';
import { ToolbarModule } from 'primeng/toolbar';
import { Button, ButtonModule } from 'primeng/button';
import { NgModel, FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchChangeEvent, ToggleSwitchModule } from 'primeng/toggleswitch';
import { TagModule } from 'primeng/tag';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { BaseHttpService } from '../../core/services/base-http.service';
import { BehaviorSubject, catchError, combineLatest, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { StateService } from '../../shared/services/state.service';
import { TopicClassification } from '../../core/interfaces/TopicClassification';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ToggleButtonModule } from 'primeng/togglebutton';

@Component({
  selector: 'app-topic-classification',
  standalone: true,
  imports: [
    TableModule,
    NgIf,
    ToolbarModule,
    Ripple,
    Dialog,
    TextareaModule,
    Button,
    FormsModule,
    InputTextModule,
    CommonModule,
    ConfirmDialog,
    ToastModule,
    ToggleSwitchModule,
    TagModule,
    ButtonModule,
    ToggleSwitch,
    ProgressSpinner,
    ToggleButtonModule
  ],
  templateUrl: './topic-classification.component.html',
  styleUrl: './topic-classification.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class TopicClassificationComponent implements OnInit {



  // Dependency Injections
  private _messageService = inject(MessageService);
  private _confirmationService = inject(ConfirmationService);
  private _baseHttpService = inject(BaseHttpService);
  private _stateService = inject(StateService);

  private justSavedSubject = new BehaviorSubject<boolean>(false);
  private justSaved$ = this.justSavedSubject.asObservable();


  topics = computed(() => this._stateService.topicClassificationTableIncoming());

  // Private Properties
  private clonedTopics: { [s: string]: TopicClassification } = {};
  refresh$ = this._stateService.topicsWebSocketNotification$.pipe(
    withLatestFrom(this.justSaved$),
    map(([wsNotification, justSaved]) => {
      if (justSaved) {
        this.justSavedSubject.next(false);
        return false;
      }
      return wsNotification;
    }),
    tap((flag) => {
      if (flag) {
        this._stateService.promptTopicsRefresh();
      } else {
        this._stateService.confirmTopicsRefresh();
      }
    })
  );

  getTables$ = this._stateService.getTables$;

  // Constructor
  constructor() {
    effect(() => {

      const currentActive = this.topicsActive();

    });

    effect(() => {
      this.isActive = this.topicsActive();
    })
  }

  // Public Properties
  class: KeyArea = '';


  selectedTopics!: TopicClassification[] | null;
  classificationDict = [
    { label: 'Competitors', value: 'Competitors' },
    { label: 'Account', value: 'Account' },
    { label: 'Products', value: 'Products' },
    { label: 'Billing', value: 'Billing' },
    { label: 'Churn', value: 'Churn' },
  ];
  deletedRows = signal<string[]>([]);
  addedRows = signal<string[]>([]);
  changedRows = signal<string[]>([]);
  addNewRowDialog = signal(false);
  submitted = signal(false);
  deletedRowsCount = signal<number>(0);
  tableStatusChanged = signal<boolean>(false);

  pendingSave = computed(
    () =>
      (this.changedRows().length > 0 || this.deletedRows().length > 0 || this.addedRows().length > 0) &&
      !this.awaitingRefresh()
  );

  topicClassificationTableIncoming = this._stateService.topicClassificationTableIncoming
  awaitingRefresh = this._stateService.awaitingTopicsRefresh;
  isLoading = this._stateService.isLoading;
  topicsActive = this._stateService.topicsActive;
  isActive = this.topicsActive();

  topic!: Partial<TopicClassification>;
  newTopicClassification: WritableSignal<TopicClassification> = signal({
    topic: '',
    en_definition: '',
    gr_definition: '',
    status: this.isActive,
  });


  @ViewChild('text2') text2!: ElementRef;
  @ViewChild('text3') text3!: ElementRef;
  @ViewChild('text4') text4!: ElementRef;
  @ViewChild('text5') text5!: ElementRef;



  // Angular Lifecycle Hooks
  ngOnInit(): void { }

  // Public Methods
  getSeverity(status: string) {
    switch (status) {
      case 'unqualified':
        return 'danger';

      case 'qualified':
        return 'success';

      case 'new':
        return 'info';

      case 'negotiation':
        return 'warn';

      case 'renewal':
        return null;

      default:
        return 'contrast';
    }
  }

  hideDialog() {
    if (this.submitted()) {
      // Get current length of topics array (will be index of new row)
      const newRowIndex = this.topics().length;

      this._stateService.updateTopics({
        topicClassificationTableIncoming: [
          ...this.topics(),
          this.newTopicClassification(),
        ]
      });

      // Add new row index to addedRows signal
      // this.addedRows.update(rows => [...rows, newRowIndex]);

      
      this.newTopicClassification.set({
        topic: '',
        en_definition: '',
        gr_definition: '',
        status: true,
      });

      this._messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'New row created',
        life: 3000,
      });
    }

    this.addNewRowDialog.set(false);
  }

  openNew() {
    this.newTopicClassification.set({
      topic: '',
      en_definition: '',
      gr_definition: '',
      status: true,
    });
    this.submitted.set(false);
    this.addNewRowDialog.set(true);
  }

  onRowEditInit(topic: TopicClassification) {
    this.clonedTopics[topic.topic as string] = {
      ...topic,
    };
  }

  rowId(): string { 
    return crypto.randomUUID();
  }

  onRowEditSave(topic: TopicClassification, rowIndex: number) {
    if (topic.topic) {
      this.changedRows.update(rows =>
        rows.includes(topic.topic!) ? rows : [...rows, topic.topic!]
      );

      delete this.clonedTopics[topic.topic as string];
      this._messageService.add({
        severity: 'warn',
        summary: 'Success',
        detail: 'Row was updated. Save changes to persist.',
      });
    }
  }

  onRowEditCancel(topic: TopicClassification, rowIndex: number) {
    const originalValue = this.clonedTopics[topic.topic as string];
    
    topic.en_definition = originalValue.en_definition;
    topic.gr_definition = originalValue.gr_definition;
    topic.status = originalValue.status;
    
    delete this.clonedTopics[topic.topic as string];
    
    this.changedRows.update(rows => 
      rows.filter(row => row !== topic.topic)
    );
  }

  saveRow() {
    this.newTopicClassification.update(() => ({
      topic: this.class,
      gr_definition: this.text2.nativeElement.value,
      en_definition: this.text3.nativeElement.value,
      status: true,
    }));

    this.submitted.set(true);

    if (
      this.newTopicClassification().topic &&
      this.newTopicClassification().gr_definition &&
      this.newTopicClassification().en_definition
    ) {

      const newRowIndex = this.topics().length;  

      this._stateService.updateTopics({
        topicClassificationTableIncoming: [
          ...this.topics(),
          this.newTopicClassification(),
        ]
      });

      this.addedRows.update(rows => [...rows, this.newTopicClassification().topic!]);

      this.addNewRowDialog.set(false);
      this.newTopicClassification.set({
        topic: '',
        en_definition: '',
        gr_definition: '',
        status: true,
      });
      this._messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: 'New row created',
        life: 3000,
      });
    }

    this.addedRows.update(() => [...this.addedRows()]);
  }

  deleteSelectedTopics() {
    const savedRows = this.selectedTopics?.filter(
      topic => !this.addedRows().includes(topic.topic!)
    ) || [];

    // Only show confirmation if there are saved rows to delete
    if (savedRows.length > 0) {
      this._confirmationService.confirm({
        message: `Delete the <b> ${savedRows
          .map((topic) => topic.topic)
          .join(', ')} </b> classification(s)? 
          <br><b severity='danger'>Save changes to persist.</b>`,
        header: 'Confirm',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deletedRows.update(current => [
            ...current,
            ...savedRows.map(topic => topic.topic!)
          ]);

          this.deletedRowsCount.update(count => count + savedRows.length);

          this.addedRows.update(rows =>
            rows.filter(row => !this.selectedTopics?.map(t => t.topic).includes(row))
          );

          const updatedTopics = this.topics().filter(
            (topic) => !this.selectedTopics?.includes(topic)
          );

          this._stateService.updateTopics({
            topicClassificationTableIncoming: updatedTopics,
          });

          this.selectedTopics = null;

          if (savedRows.length > 0) {
            this._messageService.add({
              severity: 'success',
              summary: 'Successful',
              detail: `${savedRows.length} Topic Classification(s) Deleted`,
              life: 3000,
            });
          }
        }
      });
    } else {
      this.addedRows.update(rows =>
        rows.filter(row => !this.selectedTopics?.map(t => t.topic).includes(row))
      );

      const updatedTopics = this.topics().filter(
        (topic) => !this.selectedTopics?.includes(topic)
      );

      this._stateService.updateTopics({
        topicClassificationTableIncoming: updatedTopics,
      });

      this.selectedTopics = null;
    }
  }

  saveChanges() {
    this._confirmationService.confirm({
      message: 'Are you sure you want to save all changed cells?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Save',
        severity: 'success',
      },
      accept: () => {
        this._baseHttpService.sendTopicClassificationUpdate({
          table_name: 'nrg_exp_topic_classification_definitions',
          rows: this.topicClassificationTableIncoming(),
        }).subscribe(
          data => {
            this.changedRows.update(() => []);
            this.deletedRowsCount.set(0); // Reset count after successful save
            this.tableStatusChanged.set(false); // Reset status changed flag
            this._messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Table was updated',
            });
            this.justSavedSubject.next(true);
          }
        );
      },
    });

    this.addedRows.set([]);
  }

  isRowChanged(topic: string): boolean {
    return this.changedRows().includes(topic);
  }

  isRowAdded(topic: string): boolean {
    return this.addedRows().includes(topic);
  }

  getNew() {
    this._stateService.getFreshData();
  }

  changeTopicsStatus() {
    this._stateService.updateTopicStatus(!this.topicsActive());
    this.tableStatusChanged.set(true);
  }

}
